import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { MetricRecord, QueryMetricsRequest } from "../MetricRecord";

import { hashMetricsQueryKey, metricsQueryKeys } from "./metricsQueryKeys";

export interface UseInfiniteQueryMetricsOptions {
  /**
   * While a re-sorted query's first page loads, keep returning the previous
   * query's pages (with `isPlaceholderData: true`) instead of going back to a
   * pending state, so a sortable table doesn't flash a spinner.
   *
   * Applies only when the previous request differs from the new one in nothing
   * but `sort_by` / `descending`. Any other change (window, filter, metric,
   * org, or an `undefined` request in between) still starts empty, so rows
   * from one selection never stand in for another's.
   */
  keepPagesAcrossSortChange?: boolean;
}

/**
 * Paginated raw query for stored metric values whose session time falls in a
 * range, following `next_token` on demand. Returns all fetched pages on
 * `data.pages`.
 */
export function useInfiniteQueryMetrics(
  request: QueryMetricsRequest | undefined,
  orgId: string | undefined,
  options: UseInfiniteQueryMetricsOptions = {},
) {
  const { metricsService } = useDomainServices();
  const queryKey = metricsQueryKeys.queriesInfinite(request, orgId);

  return useInfiniteQuery<PaginatedResponse<MetricRecord>, Error>({
    queryKey,
    queryKeyHashFn: hashMetricsQueryKey,
    queryFn: ({ signal, pageParam }) => {
      if (request === undefined) {
        return Promise.reject(new Error("Missing query request"));
      }
      return metricsService.queryMetrics(request, {
        abortSignal: signal,
        orgId,
        pageToken: pageParam as string | undefined,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_token ?? undefined,
    enabled: request !== undefined && orgId !== undefined,
    staleTime: 60_000,
    placeholderData: (previousData, previousQuery) =>
      options.keepPagesAcrossSortChange &&
      request !== undefined &&
      previousQuery !== undefined &&
      hashIgnoringSort(previousQuery.queryKey) === hashIgnoringSort(queryKey)
        ? previousData
        : undefined,
  });
}

/**
 * Hash a `queriesInfinite` key with its request's sort fields dropped, so two
 * keys hash equal exactly when they differ only in sort. The request is the
 * key's last element (see {@link metricsQueryKeys.queriesInfinite}).
 */
function hashIgnoringSort(key: QueryKey): string {
  const request = key.at(-1) as QueryMetricsRequest | undefined;
  if (request === undefined) {
    return hashMetricsQueryKey(key);
  }
  // `undefined` members are dropped by the JSON hash, same as absent ones.
  const unsorted = { ...request, sort_by: undefined, descending: undefined };
  return hashMetricsQueryKey([...key.slice(0, -1), unsorted]);
}
